import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaHome,
  FaArrowRight,
  FaUser,
  FaFileAlt,
  FaEnvelope,
  FaQuoteLeft
} from "react-icons/fa";
import { BsGridFill } from "react-icons/bs";
import "./NotFound.css";
import astronautScene from "../assets/images/astronaut-scene-smooth.png";
import fourOhFourImg from "../assets/images/space-404-unmultiplied.png";

const quickLinksData = [
  {
    title: "About",
    description: "Know more about me",
    path: "/about",
    color: "blue",
    icon: <FaUser className="quicklink-icon" />
  },
  {
    title: "Projects",
    description: "Explore my work",
    path: "/projects",
    color: "teal",
    icon: <BsGridFill className="quicklink-icon" />
  },
  {
    title: "Certificates",
    description: "View my achievements",
    path: "/certificates",
    color: "amber",
    icon: (
      <svg
        className="quicklink-icon"
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2.5L17.5 9H13V4.5zM8 12h8v1.5H8V12zm0 3h5v1.5H8V15z" />
        <circle cx="16.5" cy="17.5" r="2.8" />
        <path d="M15.5 19.5l-1 2 2-.6 2 .6-1-2" />
      </svg>
    )
  },
  {
    title: "Blogs",
    description: "Read my articles",
    path: "/blogs",
    color: "magenta",
    icon: <FaFileAlt className="quicklink-icon" />
  },
  {
    title: "Contact",
    description: "Get in touch",
    path: "/contact",
    color: "purple",
    icon: <FaEnvelope className="quicklink-icon" />
  }
];

const NotFound = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Add page-specific scoped class to body when on 404 page
    document.body.classList.add("on-404-page");
    window.scrollTo(0, 0);

    return () => {
      document.body.classList.remove("on-404-page");
    };
  }, []);

  return (
    <div className="space-404-wrapper">
      {/* Dynamic Starfield Background */}
      <div className="space-starfield" aria-hidden="true">
        <div className="space-shooting-star"></div>
        <div className="space-shooting-star delay-1"></div>
        <div className="space-star star-1"></div>
        <div className="space-star star-2"></div>
        <div className="space-star star-3"></div>
        <div className="space-star star-4"></div>
        <div className="space-star star-5"></div>
        <div className="space-star star-6"></div>
        <div className="space-star star-7"></div>
        <div className="space-star star-8"></div>
        <div className="space-star star-9"></div>
        <div className="space-star star-10"></div>
        <div className="space-star star-11"></div>
        <div className="space-star star-12"></div>
        <div className="space-star star-13"></div>
        <div className="space-star star-14"></div>
        <div className="space-star star-15"></div>
      </div>

      <div className="space-404-container">
        {/* Main Error Hero Section */}
        <section className="space-hero-section" aria-label="Error Hero">
          {/* Left Column: Astronaut Space Illustration */}
          <div className="space-illustration-col">
            <div className="space-illustration-frame">
              <img
                src={astronautScene}
                alt="Astronaut sitting on asteroid with wooden sign and laptop lost in space"
                className="space-astronaut-image"
                loading="eager"
              />
            </div>
          </div>

          {/* Right Column: Error Content */}
          <div className="space-content-col">
            {/* LOST IN SPACE Badge */}
            <div className="space-lost-badge">
              <span className="space-badge-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <ellipse
                    cx="12"
                    cy="12"
                    rx="9"
                    ry="3.6"
                    stroke="#818cf8"
                    strokeWidth="1.8"
                    transform="rotate(-22 12 12)"
                  />
                  <circle cx="12" cy="12" r="5" fill="url(#saturnGradient)" />
                  <defs>
                    <radialGradient id="saturnGradient" cx="35%" cy="35%" r="65%">
                      <stop offset="0%" stopColor="#38bdf8" />
                      <stop offset="100%" stopColor="#818cf8" />
                    </radialGradient>
                  </defs>
                </svg>
              </span>
              <span className="space-badge-label">LOST IN SPACE</span>
            </div>

            {/* Glowing 3D 404 Typography */}
            <div className="space-404-graphic-box">
              <img
                src={fourOhFourImg}
                alt="404"
                className="space-404-graphic"
              />
              <h1 className="visually-hidden">404</h1>
            </div>

            {/* Main Error Heading */}
            <h2 className="space-error-title">
              Oops! <span className="space-title-gradient">Page Not Found</span>
            </h2>

            {/* Supporting Text */}
            <div className="space-error-desc">
              <p>Looks like this page took a different path.</p>
              <p>Let's get you back to the right place.</p>
            </div>

            {/* Main Hero Action Button: ONLY Back to Home */}
            <div className="space-hero-actions">
              <button
                type="button"
                className="space-home-button"
                onClick={() => navigate("/")}
                aria-label="Back to Home"
              >
                <FaHome className="space-btn-home-icon" aria-hidden="true" />
                <span>Back to Home</span>
                <FaArrowRight className="space-btn-arrow-icon" aria-hidden="true" />
              </button>
            </div>
          </div>
        </section>

        {/* Quick Links Section */}
        <section className="space-quicklinks-section" aria-label="Quick Links">
          <div className="space-quicklinks-divider">
            <span className="space-divider-line left" aria-hidden="true"></span>
            <h3 className="space-quicklinks-heading">Quick Links</h3>
            <span className="space-divider-line right" aria-hidden="true"></span>
          </div>

          <div className="space-quicklinks-grid">
            {quickLinksData.map((item) => (
              <Link
                key={item.title}
                to={item.path}
                className={`space-quicklink-card card-${item.color}`}
              >
                <div className="card-left">
                  <div className={`card-icon-box box-${item.color}`}>
                    {item.icon}
                  </div>
                  <div className="card-text">
                    <span className="card-title">{item.title}</span>
                    <span className="card-desc">{item.description}</span>
                  </div>
                </div>
                <div className="card-arrow" aria-hidden="true">
                  <FaArrowRight />
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Quote Section */}
        <section className="space-quote-section" aria-label="Inspirational Quote">
          <div className="space-quote-pill">
            <div className="space-quote-circle" aria-hidden="true">
              <FaQuoteLeft className="space-quote-mark" />
            </div>
            <p className="space-quote-content">
              Sometimes the wrong turns lead to the most beautiful places. ✨
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default NotFound;
